import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../services/api';
import toast from 'react-hot-toast';

const PRODUCTS = {
  resolve: 'CASA FÁCIL RESOLVE',
  'resolve-mais': 'CASA FÁCIL RESOLVE MAIS',
  'guaibim-resolve': 'GUAIBIM RESOLVE'
};

/**
 * Lets each product choose a percent or a fixed amount, on the file or the in-force base.
 */
function BillingRules() {
  const [params, setParams] = useSearchParams();
  const productId = params.get('product') || '';
  const productLabel = PRODUCTS[productId] || '';
  const [rule, setRule] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    if (!productLabel) return undefined;
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const [ruleResponse, profileResponse] = await Promise.all([
          api.get('/billing-rules', { params: { product: productId } }),
          api.get('/billing-rules/invoice-profile', { params: { product: productId } })
        ]);
        if (!active) return;
        setRule(ruleResponse.data);
        setProfile(profileResponse.data);
      } catch (error) {
        if (active) toast.error(error.response?.data?.error || 'Erro ao carregar os parâmetros');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [productId, productLabel]);

  /**
   * Stores the billed customer, payment term, method and invoice note for this product.
   */
  const saveProfile = async (event) => {
    event.preventDefault();
    const form = new FormData(event.target);
    setSavingProfile(true);
    try {
      const response = await api.put('/billing-rules/invoice-profile', {
        productId,
        legalName: form.get('legalName'),
        document: form.get('document'),
        address: form.get('address'),
        paymentTerm: form.get('paymentTerm'),
        paymentMethod: form.get('paymentMethod'),
        paymentDetail: form.get('paymentDetail'),
        invoiceNote: form.get('invoiceNote')
      });
      setProfile(response.data);
      toast.success('Cadastro da fatura salvo');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar o cadastro da fatura');
    } finally {
      setSavingProfile(false);
    }
  };

  /**
   * Stores the chosen type, amount and base. The next report reads this rule.
   */
  const saveRule = async (event) => {
    event.preventDefault();
    const form = new FormData(event.target);
    setSaving(true);
    try {
      const response = await api.put('/billing-rules', {
        productId,
        mode: form.get('mode'),
        percentRate: parseAmount(form.get('percentRate')),
        fixedAmount: parseAmount(form.get('fixedAmount')),
        scope: form.get('scope')
      });
      setRule(response.data);
      toast.success('Parâmetros salvos');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar os parâmetros');
    } finally {
      setSaving(false);
    }
  };

  if (!productLabel) {
    return (
      <Layout headerTitle="Parâmetros" headerSubtitle="Faturamento por produto">
        <div className="p-6 max-w-3xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Parâmetros de faturamento</h1>
          <p className="text-gray-600 mb-6">Escolha o produto para definir a regra e os dados da fatura.</p>
          <div className="grid gap-3">
            {Object.entries(PRODUCTS).map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => setParams({ product: id })}
                className="text-left bg-white border border-gray-200 rounded-lg px-4 py-3 hover:border-primary-300"
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout headerTitle={productLabel} headerSubtitle="Parâmetros de faturamento">
      <div className="p-6 max-w-3xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{productLabel}</h1>
          <p className="text-gray-600">
            O Excel, a fatura e o valor a faturar passam a usar esta regra no próximo relatório, sem reprocessar o arquivo.
          </p>
        </div>

        {loading || !rule || !profile ? (
          <div className="bg-white rounded-lg border border-gray-200 p-6 text-gray-600">Carregando parâmetros...</div>
        ) : (
          <form key={`${rule.productId}-${rule.updatedAt || 'new'}`} onSubmit={saveRule} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
            <fieldset>
              <legend className="text-sm font-medium text-gray-700 mb-2">Tipo</legend>
              <div className="grid sm:grid-cols-2 gap-3">
                <Choice name="mode" value="percentual" label="Percentual" detail="Soma o valor dos itens e aplica o percentual" defaultChecked={rule.mode === 'percentual'} />
                <Choice name="mode" value="fixo" label="Valor fixo" detail="Multiplica o valor informado pela quantidade de itens" defaultChecked={rule.mode === 'fixo'} />
              </div>
            </fieldset>

            <div className="grid sm:grid-cols-2 gap-4">
              <label>
                <span className="block text-sm font-medium text-gray-700 mb-1">Percentual</span>
                <input
                  name="percentRate"
                  inputMode="decimal"
                  defaultValue={formatInput(rule.percentRate)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </label>
              <label>
                <span className="block text-sm font-medium text-gray-700 mb-1">Valor fixo (R$)</span>
                <input
                  name="fixedAmount"
                  inputMode="decimal"
                  defaultValue={formatInput(rule.fixedAmount)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </label>
            </div>

            <fieldset>
              <legend className="text-sm font-medium text-gray-700 mb-2">Base</legend>
              <div className="grid gap-3">
                <Choice name="scope" value="arquivo" label="Arquivo processado" detail="Só os itens ativos daquele arquivo. Cancelamento no mesmo arquivo fica de fora" defaultChecked={rule.scope === 'arquivo'} />
                <Choice name="scope" value="vigente" label="Base vigente" detail="Todos os clientes com apólice vigente, pelo valor da última adesão" defaultChecked={rule.scope === 'vigente'} />
              </div>
            </fieldset>

            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-60"
            >
              {saving ? 'Salvando...' : 'Salvar parâmetros'}
            </button>
          </form>
        )}

        {!loading && profile && (
          <form
            key={`${profile.productId}-${profile.updatedAt || 'new'}`}
            onSubmit={saveProfile}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4"
          >
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Cadastro da fatura</h2>
              <p className="text-sm text-gray-600">
                Estes dados entram em todas as faturas deste produto. Campo vazio não aparece na fatura.
              </p>
            </div>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">Razão social</span>
              <input name="legalName" defaultValue={profile.legalName} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">CNPJ</span>
              <input name="document" defaultValue={profile.document} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">Endereço</span>
              <input name="address" defaultValue={profile.address} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
            </label>
            <div className="grid sm:grid-cols-2 gap-4">
              <label>
                <span className="block text-sm font-medium text-gray-700 mb-1">Prazo de pagamento</span>
                <input name="paymentTerm" defaultValue={profile.paymentTerm} placeholder="30 dias" className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </label>
              <label>
                <span className="block text-sm font-medium text-gray-700 mb-1">Forma de pagamento</span>
                <select name="paymentMethod" defaultValue={profile.paymentMethod} className="w-full border border-gray-300 rounded-lg px-3 py-2">
                  <option value="">Não informar</option>
                  <option value="boleto">Boleto</option>
                  <option value="pix">Pix</option>
                  <option value="transferencia">Transferência</option>
                  <option value="outro">Outro</option>
                </select>
              </label>
            </div>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">Detalhe da forma</span>
              <input name="paymentDetail" defaultValue={profile.paymentDetail} placeholder="Chave Pix, banco ou instrução" className="w-full border border-gray-300 rounded-lg px-3 py-2" />
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">Observação da NF</span>
              <textarea name="invoiceNote" defaultValue={profile.invoiceNote} rows={3} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
            </label>
            <button
              type="submit"
              disabled={savingProfile}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-60"
            >
              {savingProfile ? 'Salvando...' : 'Salvar cadastro da fatura'}
            </button>
          </form>
        )}
      </div>
    </Layout>
  );
}

/**
 * Renders one radio option for the billing type or the billing base.
 */
function Choice({ name, value, label, detail, defaultChecked }) {
  return (
    <label className="flex gap-3 border border-gray-200 rounded-lg p-3 cursor-pointer hover:border-primary-300">
      <input type="radio" name={name} value={value} defaultChecked={defaultChecked} className="mt-1" />
      <span>
        <span className="block font-medium text-gray-900">{label}</span>
        <span className="block text-sm text-gray-600">{detail}</span>
      </span>
    </label>
  );
}

/**
 * Reads a Brazilian or dotted decimal into a number.
 */
function parseAmount(value) {
  const raw = String(value || '').trim().replace(/\s/g, '');
  if (raw === '') return 0;
  const normalized = raw.includes(',') ? raw.replace(/\./g, '').replace(',', '.') : raw;
  const amount = Number(normalized);
  return Number.isFinite(amount) ? amount : NaN;
}

/**
 * Shows a stored number with a comma decimal separator.
 */
function formatInput(value) {
  return String(value ?? 0).replace('.', ',');
}

export default BillingRules;
